import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import toast from 'react-hot-toast';
import { TrendingUp, RefreshCw, Search, Calendar, CheckCircle, AlertCircle, Minus } from 'lucide-react';

export default function AdminProfits() {
  const [profits, setProfits] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [todaySummary, setTodaySummary] = useState(null);
  const [filters, setFilters] = useState({ userId: '', startDate: '', endDate: '' });
  const [stats, setStats] = useState({});

  const fetchProfits = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filters.userId) params.append('userId', filters.userId);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      const { data } = await axios.get(`/api/profit/admin/all?${params}`);
      setProfits(data.profits || []);
      setTotal(data.total || 0);
      setStats(data.stats || {});
    } finally {
      setLoading(false);
    }
  };

  const fetchTodaySummary = async () => {
    try {
      const { data } = await axios.get('/api/profit/admin/today-summary');
      setTodaySummary(data);
    } catch (e) {}
  };

  useEffect(() => { fetchProfits(); fetchTodaySummary(); }, [page, filters]);

  const handleProcessNow = async () => {
    if (!window.confirm('Process profit for all active users now? This normally runs automatically at 1:00 AM.')) return;
    setProcessing(true);
    try {
      await axios.post('/api/profit/admin/process-now');
      toast.success('Processing has started! It will complete shortly.');
      setTimeout(() => { fetchProfits(); fetchTodaySummary(); }, 5000);
    } catch (err) {
      toast.error('Processing failed to start');
    } finally {
      setProcessing(false);
    }
  };

  const statusBadge = {
    processed: { text: 'Processed', class: 'bg-primary-500/20 text-primary-300', icon: <CheckCircle className="w-3 h-3" /> },
    insufficient_wallet: { text: 'Low Wallet', class: 'bg-red-500/20 text-red-300', icon: <AlertCircle className="w-3 h-3" /> },
    no_profit: { text: 'No Profit', class: 'bg-gray-500/20 text-gray-400', icon: <Minus className="w-3 h-3" /> }
  };

  return (
    <AdminLayout>
      <div className="animate-fade-in">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-2xl font-bold text-white">Daily Profits</h1>
          <button
            onClick={async () => {
              try {
                const res = await require('axios').default.post('/api/mt5/admin/sync-all');
                alert(res.data.message);
              } catch(e) {
                alert(e.response?.data?.message || 'Error');
              }
            }}
            className="flex items-center gap-2 bg-blue-600/20 border border-blue-500/30 text-blue-400 hover:bg-blue-600/30 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors">
            ↻ Sync All MT5 Users Now
          </button>
        </div>
            <p className="text-gray-400 mt-1">Automatically fetched from MT5 — processed automatically at 1:00 AM IST.</p>
          </div>
          <button
            onClick={handleProcessNow}
            disabled={processing}
            className="bg-primary-600 hover:bg-primary-500 disabled:bg-primary-800 text-white px-4 py-2.5 rounded-xl font-medium flex items-center gap-2 transition-colors">
            {processing
              ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              : <RefreshCw className="w-4 h-4" />}
            {processing ? 'Processing...' : 'Process Now (Manual)'}
          </button>
        </div>

        {/* Today Summary */}
        {todaySummary && (
          <div className="bg-dark-700 border border-white/5 rounded-2xl p-5 mb-6">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-primary-400" />
              Today's Summary ({todaySummary.totalProcessed} users processed)
            </h3>
            <div className="grid grid-cols-3 gap-4">
              {(todaySummary.summary || []).map(s => (
                <div key={s._id} className="bg-dark-600 rounded-xl p-3 text-center">
                  <p className="text-white font-bold">{s.count}</p>
                  <p className="text-gray-400 text-xs capitalize mt-0.5">{s._id?.replace('_', ' ')}</p>
                  {s.totalDeducted > 0 && (
                    <p className="text-red-400 text-xs mt-1">${s.totalDeducted.toFixed(2)} deducted</p>
                  )}
                </div>
              ))}
              {(todaySummary.summary || []).length === 0 && (
                <div className="col-span-3 text-center text-gray-500 py-4">
                  No profit has been processed yet today.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Overall Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-dark-700 border border-white/5 rounded-2xl p-4">
            <p className="text-gray-400 text-sm">Total Gross Profit</p>
            <p className="text-2xl font-bold text-primary-400">${(stats.totalGross || 0).toFixed(2)}</p>
          </div>
          <div className="bg-dark-700 border border-white/5 rounded-2xl p-4">
            <p className="text-gray-400 text-sm">Total Deducted (40%)</p>
            <p className="text-2xl font-bold text-red-400">${(stats.totalDeducted || 0).toFixed(2)}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-dark-700 border border-white/5 rounded-2xl p-4 mb-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">From Date</label>
              <input type="date" value={filters.startDate}
                onChange={e => setFilters(f => ({ ...f, startDate: e.target.value }))}
                className="bg-dark-600 border border-white/10 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-primary-500" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">To Date</label>
              <input type="date" value={filters.endDate}
                onChange={e => setFilters(f => ({ ...f, endDate: e.target.value }))}
                className="bg-dark-600 border border-white/10 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-primary-500" />
            </div>
            <button onClick={() => setFilters({ userId: '', startDate: '', endDate: '' })}
              className="px-3 py-2 bg-dark-600 border border-white/10 text-gray-400 hover:text-white rounded-xl text-sm transition-colors">
              Clear
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-dark-700 border border-white/5 rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-white/5 flex items-center justify-between">
            <h3 className="text-white font-semibold">Profit Records ({total})</h3>
            <p className="text-gray-500 text-xs">Auto-fetched from MT5 — updates automatically at 1:00 AM.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left px-4 py-3 text-gray-400 font-medium">User</th>
                  <th className="text-left px-4 py-3 text-gray-400 font-medium">Date</th>
                  <th className="text-right px-4 py-3 text-gray-400 font-medium">Opening</th>
                  <th className="text-right px-4 py-3 text-gray-400 font-medium">Closing</th>
                  <th className="text-right px-4 py-3 text-gray-400 font-medium">Gross Profit</th>
                  <th className="text-right px-4 py-3 text-gray-400 font-medium">40% Deducted</th>
                  <th className="text-right px-4 py-3 text-gray-400 font-medium">Wallet After</th>
                  <th className="text-center px-4 py-3 text-gray-400 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="text-center py-12">
                    <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  </td></tr>
                ) : profits.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-12 text-gray-500">
                    No profit records found
                  </td></tr>
                ) : profits.map(p => {
                  const badge = statusBadge[p.status] || statusBadge.processed;
                  return (
                    <tr key={p._id} className="border-b border-white/5 hover:bg-white/2 transition-colors">
                      <td className="px-4 py-3">
                        <p className="text-white text-sm font-medium">{p.user?.name}</p>
                        <p className="text-gray-500 text-xs">{p.user?.mt5Login || p.user?.email}</p>
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-sm">
                        {new Date(p.date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-400">${(p.openingEquity||0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-gray-400">${(p.closingEquity||0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-primary-400 font-semibold">
                        {p.grossProfit > 0 ? `+$${p.grossProfit.toFixed(2)}` : '$0'}
                      </td>
                      <td className="px-4 py-3 text-right text-red-400 font-semibold">
                        {p.deductionAmount > 0 ? `-$${p.deductionAmount.toFixed(2)}` : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-semibold text-sm ${(p.walletAfter||0) >= 0 ? 'text-white' : 'text-red-400'}`}>
                          ${(p.walletAfter||0).toFixed(2)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.class}`}>
                          {badge.icon} {badge.text}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {total > 20 && (
            <div className="p-4 border-t border-white/5 flex justify-between items-center">
              <p className="text-gray-400 text-sm">Total: {total}</p>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1}
                  className="px-3 py-1.5 bg-dark-600 text-white rounded-lg text-sm disabled:opacity-50">Prev</button>
                <button onClick={() => setPage(p => p+1)} disabled={page*20>=total}
                  className="px-3 py-1.5 bg-dark-600 text-white rounded-lg text-sm disabled:opacity-50">Next</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
